/**
 * useKeyboardShortcuts (Sprint 171 D-003)
 *
 * Reusable keyboard-shortcut primitive. Binds a map of single-key or chord
 * shortcuts onto either a scoped container (default) or the document. The
 * hook is deliberately minimal -- it does not manage any visual state or
 * render anything. Callers are responsible for updating their own React
 * state from the action callbacks.
 *
 * Accessibility gating (MANDATORY):
 *   - Shortcuts are ignored when focus is inside an input, textarea, select,
 *     or contenteditable element, EXCEPT for shortcuts whose action target is
 *     that same field (we leave that judgement to the caller via the
 *     `allowInFormFields` opt-in per binding).
 *   - Shortcuts are ignored while any element with `aria-modal="true"` is in
 *     the document -- i.e. while a Dialog is open -- unless the binding opts
 *     in with `allowWhenModalOpen`. Escape is the canonical example.
 *   - When a containerRef is provided, the shortcut only fires if the active
 *     element is inside the container or the focus is on document.body (so
 *     that the user can still hit shortcuts without having to click into the
 *     page first). This prevents a j/k binding on the task queue from
 *     firing when the user is navigating an unrelated page that reuses this
 *     hook via another mount.
 *
 * Chords: A binding key of the form "g f" declares a two-stroke chord
 * (leader key "g" followed by "f" within the chord window). Single-stroke
 * bindings always take precedence over chord leaders when both exist.
 */

import { useEffect, useRef, type RefObject } from 'react';

/** How long (ms) the chord leader stays armed while waiting for the second key. */
const CHORD_WINDOW_MS = 1_000;

export interface KeyboardShortcutConfig {
  /**
   * Key spec. Either:
   *   - a single key ("j", "?", "Escape", "/") -- `event.key` is compared
   *     case-insensitively for letters, exactly for symbols.
   *   - a chord in the form "g f" -- two keys separated by a single space.
   */
  keys: string;
  /** Callback invoked when the shortcut matches. */
  handler: (event: KeyboardEvent) => void;
  /** Short human-readable description for help overlays. */
  description: string;
  /** If true, do not ignore the shortcut when focus is in a form field. */
  allowInFormFields?: boolean;
  /** If true, do not ignore the shortcut when a Dialog/modal is open. */
  allowWhenModalOpen?: boolean;
  /** Disable this binding without removing it from the map. */
  disabled?: boolean;
}

export interface UseKeyboardShortcutsOptions {
  /**
   * Container ref. When provided, shortcuts only fire when the active element
   * is inside this container OR is the body (no explicit focus). Omit to
   * bind document-wide.
   */
  containerRef?: RefObject<HTMLElement | null>;
  /** Globally disable the hook without tearing it down. */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests)
// ---------------------------------------------------------------------------

/**
 * True when the event target is a form field or a contenteditable element.
 * Used by the hook to gate shortcuts that should not fire while typing.
 */
export function isFormFieldTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * True when any element in the document advertises `aria-modal="true"`.
 * React-Aria-Components Dialog renders `aria-modal` on the dialog element,
 * so this reliably detects our Dialog primitive being open.
 */
export function isModalOpen(doc: Document = document): boolean {
  return doc.querySelector('[aria-modal="true"]') !== null;
}

/**
 * Normalise a key string for comparison. Letter keys are lower-cased so
 * "j" matches both 'j' and 'J' events. Non-letter keys (like "Escape" or
 * "/") are returned as-is.
 */
export function normaliseKey(key: string): string {
  if (key.length === 1 && /[a-z]/i.test(key)) return key.toLowerCase();
  return key;
}

/**
 * Returns the list of [spec, def] entries whose KEY field is a chord
 * ("g f"). Exposed for tests.
 */
export function isChord(spec: string): boolean {
  return spec.includes(' ');
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Bind a map of keyboard shortcuts. Bindings are stable via a ref so the
 * effect only re-runs when the container ref / disabled flag change.
 */
export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcutConfig[],
  options: UseKeyboardShortcutsOptions = {},
): void {
  const { containerRef, disabled } = options;
  const shortcutsRef = useRef<KeyboardShortcutConfig[]>(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    if (disabled) return undefined;

    // Chord state: when a leader key is pressed, we arm it here and wait
    // for the follow-up key within CHORD_WINDOW_MS.
    let chordLeader: string | null = null;
    let chordTimer: ReturnType<typeof setTimeout> | null = null;

    const clearChord = (): void => {
      chordLeader = null;
      if (chordTimer !== null) {
        clearTimeout(chordTimer);
        chordTimer = null;
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      // Ignore modifier-prefixed combos. The current binding set does not
      // use Ctrl/Cmd/Alt/Meta, so any such press belongs to the browser or
      // OS and must not be intercepted.
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const active = event.target;
      const inFormField = isFormFieldTarget(active);
      const modalOpen = isModalOpen();

      // Scope gating: when a containerRef is provided, require focus to be
      // inside the container OR on document.body (no explicit focus).
      if (containerRef?.current) {
        const activeEl = document.activeElement;
        const focusIsInScope =
          activeEl === document.body ||
          (activeEl instanceof Node && containerRef.current.contains(activeEl));
        if (!focusIsInScope) return;
      }

      const key = normaliseKey(event.key);
      const current = shortcutsRef.current;

      // Resolve chord continuation first -- if a leader is armed, only a
      // matching follow-up is interesting. Anything else resets the chord.
      if (chordLeader !== null) {
        const chordSpec = `${chordLeader} ${key}`;
        const match = current.find(
          (s) => !s.disabled && s.keys === chordSpec,
        );
        clearChord();
        if (match) {
          if (inFormField && !match.allowInFormFields) return;
          if (modalOpen && !match.allowWhenModalOpen) return;
          event.preventDefault();
          match.handler(event);
        }
        return;
      }

      // Single-key match takes precedence over chord leaders.
      const single = current.find(
        (s) => !s.disabled && !isChord(s.keys) && normaliseKey(s.keys) === key,
      );
      if (single) {
        if (inFormField && !single.allowInFormFields) return;
        if (modalOpen && !single.allowWhenModalOpen) return;
        event.preventDefault();
        single.handler(event);
        return;
      }

      // Chord leader? If any chord binding starts with this key, arm it.
      const isLeader = current.some((s) => {
        if (s.disabled) return false;
        if (!isChord(s.keys)) return false;
        return normaliseKey(s.keys.split(' ')[0] ?? '') === key;
      });
      if (isLeader) {
        // Do not arm chord leaders while in a form field or modal unless any
        // matching chord explicitly allows it. We conservatively require
        // both gates to be clear; otherwise typing "g" in a field would
        // silently swallow the next keystroke.
        if (inFormField || modalOpen) return;
        chordLeader = key;
        chordTimer = setTimeout(clearChord, CHORD_WINDOW_MS);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
      clearChord();
    };
  }, [containerRef, disabled]);
}

export default useKeyboardShortcuts;
